import React, { useState } from 'react';
import { Button, Icon, toast } from '@tviklax/ui-components';
import { maerskService, MaerskQuoteRequest } from '../../../services/maersk';
import { OrderFormData } from './types';
import styled from 'styled-components';

const QuoteCard = styled.div<{ selected?: boolean }>`
  border: 1px solid ${props => props.selected ? '#f86400' : '#e2e8f0'};
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 12px;
  cursor: pointer;
  transition: all 0.2s;
  background: ${props => props.selected ? '#fff7ed' : '#ffffff'};
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;

  &:hover {
    border-color: #f86400;
    box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1);
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const QuoteInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const QuotePrice = styled.div`
  font-size: 1.25rem;
  font-weight: 700;
  color: #1e293b;
`;

const QuoteBadge = styled.span`
  background: #f86400;
  color: white;
  padding: 2px 8px;
  border-radius: 9999px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
`;

const QuotesHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;

    button {
      width: 100%;
    }
  }
`;

interface MaerskQuotesProps {
    formData: OrderFormData;
    onSelect: (quote: any) => void;
}

export const MaerskQuotes: React.FC<MaerskQuotesProps> = ({ formData, onSelect }) => {
    const [loading, setLoading] = useState(false);
    const [quotes, setQuotes] = useState<any[]>([]);
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const parseTransitDays = (transitTime?: string): number => {
        const value = String(transitTime || '').toLowerCase();
        const match = value.match(/(\d+)/);
        return match ? Number(match[1]) : Number.MAX_SAFE_INTEGER;
    };
    const fastestQuoteId = quotes.reduce((bestId: string | null, quote: any) => {
        if (!quote?.id) return bestId;
        if (!bestId) return quote.id;
        const currentBest = quotes.find((q) => q.id === bestId);
        return parseTransitDays(quote.transitTime) < parseTransitDays(currentBest?.transitTime)
            ? quote.id
            : bestId;
    }, null);

    const fetchRates = async () => {
        if (!formData.customerCity || !formData.deliveryCity || !formData.weight) {
            toast({
                title: 'Missing Information',
                description: 'Please ensure City, Delivery City, and Weight are filled.',
                variant: 'destructive',
            });
            return;
        }

        setLoading(true);
        try {
            const request: MaerskQuoteRequest = {
                origin: { city: formData.customerCity, countryCode: formData.customerCountry },
                destination: { city: formData.deliveryCity, countryCode: formData.deliveryCountry },
                containers: [{ type: formData.packageType || '20GP', quantity: 1 }],
                cargoType: 'GENERAL',
                weight: parseFloat(formData.weight) || 0,
            };

            const results = await maerskService.getQuotes(request);
            setQuotes(results);
            if (results.length > 0) {
                toast({
                    title: 'Rates Fetched',
                    description: `Found ${results.length} available Maersk services.`,
                });
            }
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Failed to fetch Maersk rates. Please try again.';
            toast({
                title: 'API Error',
                description: message,
                variant: 'destructive',
            });
        } finally {
            setLoading(false);
        }
    };

    const handleSelect = (quote: any) => {
        setSelectedId(quote.id);
        onSelect(quote);
    };

    return (
        <div style={{ marginTop: '24px', borderTop: '1px solid #e2e8f0', paddingTop: '24px' }}>
            <QuotesHeader>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Icon name="Zap" color="#f86400" size={20} />
                    <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#1e293b' }}>Maersk Logistics Rates</h3>
                </div>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={fetchRates}
                    disabled={loading}
                >
                    {loading ? 'Fetching rates...' : 'Fetch Rates'}
                </Button>
            </QuotesHeader>

            {loading && (
                <div style={{ padding: '2rem', textAlign: 'center' }}>
                    <div style={{ marginBottom: '1rem', color: '#64748b' }}>Fetching rates from logistics provider...</div>
                    {/* Assuming Loading component exists in ui-components or just using a pulse */}
                    <div style={{ width: '40px', height: '40px', border: '3px solid #f86400', borderTopColor: 'transparent', borderRadius: '50%', margin: '0 auto', animation: 'spin 1s linear infinite' }} />
                    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                </div>
            )}

            {!loading && quotes.length > 0 && (
                <div>
                    {quotes.map((quote) => (
                        <QuoteCard
                            key={quote.id}
                            selected={selectedId === quote.id}
                            onClick={() => handleSelect(quote)}
                        >
                            <QuoteInfo>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <span style={{ fontWeight: 600, color: '#475569' }}>{quote.service}</span>
                                    {quote.id === fastestQuoteId && <QuoteBadge>Fastest</QuoteBadge>}
                                </div>
                                <div style={{ fontSize: '13px', color: '#64748b' }}>
                                    Transit Time: {quote.transitTime} | ETD: {quote.etd}
                                </div>
                            </QuoteInfo>
                            <QuotePrice>{quote.currency} {quote.price.toLocaleString()}</QuotePrice>
                        </QuoteCard>
                    ))}
                </div>
            )}

            {!loading && quotes.length === 0 && !loading && (
                <div style={{ padding: '1rem', background: '#f8fafc', borderRadius: '8px', textAlign: 'center', color: '#64748b', fontSize: '14px' }}>
                    Enter origin, destination, and weight to see available Maersk shipping options.
                </div>
            )}
        </div>
    );
};
